import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { Pencil, Link as LinkIcon } from 'lucide-react';
import { Model, Video, ModelSpace } from '../types/model';
import { modelService } from '../services/modelService';
import { videoService } from '../services/videoService';
import { Note, getNotes, createNote, deleteNote } from '../services/noteService';
import api from '../services/api';
import SortableList from './SortableList';
import AddItemsModal from './AddItemsModal';
import RichTextEditor from './RichTextEditor';
import ConfirmDialog from './ConfirmDialog';
import PhotoGallery from './PhotoGallery';
import DocumentGallery from './DocumentGallery';
import VideoGallery, { VideoGalleryHandle } from './VideoGallery';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';
import '../styles/ModelDetailsSlider.css';

interface ModelDetailsSliderProps {
  workspaceId: string;
  modelId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const ModelDetailsSlider: React.FC<ModelDetailsSliderProps> = ({
  workspaceId,
  modelId,
  onClose,
  onSuccess
}) => {
  const [model, setModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>('');

  // Basic Information editing
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [basicInfoData, setBasicInfoData] = useState({
    name: '',
    bed: '',
    bath: '',
    area: '',
    outdoorType: '',
    outdoorArea: '',
    ceiling: '',
    type: '',
    modelCollection: '',
    den: '',
    media: '',
    flex: ''
  });
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);

  // Views editing
  const [editingViews, setEditingViews] = useState(false);
  const [viewsData, setViewsData] = useState<string[]>([]);
  const [savingViews, setSavingViews] = useState(false);
  const [showAddViewsModal, setShowAddViewsModal] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<ModelSpace[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Notes management
  const [notes, setNotes] = useState<Note[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);

  // Video management
  const [videos, setVideos] = useState<Video[]>([]);
  const videoGalleryRef = React.useRef<VideoGalleryHandle>(null);

  // Custom Attributes editing
  const [editingCustomAttributes, setEditingCustomAttributes] = useState(false);
  const [customAttributesData, setCustomAttributesData] = useState<Array<{ key: string; value?: string }>>([]);
  const [savingCustomAttributes, setSavingCustomAttributes] = useState(false);

  // Delete model
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Project views/spaces (fetched from project)
  const [projectViews, setProjectViews] = useState<string[]>([]);
  const [projectSpaces, setProjectSpaces] = useState<string[]>([]);

  useEffect(() => {
    fetchModel();
    fetchNotes();
  }, [modelId]);

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchModel = async () => {
    try {
      setLoading(true);
      const [modelResponse, videosData] = await Promise.all([
        modelService.getById(workspaceId, modelId),
        videoService.getVideos(workspaceId, 'model', modelId)
      ]);
      setModel(modelResponse.model);
      setVideos(videosData || []);

      // Fetch project views and spaces
      if (modelResponse.model?.projectId) {
        fetchProjectViewsAndSpaces(modelResponse.model.projectId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch model details');
      console.error('Error fetching model:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectViewsAndSpaces = async (projectId: string) => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects/${projectId}`);
      const project = response.data.project;
      setProjectViews(project.views || []);
      setProjectSpaces(project.spaces || []);
    } catch (err: any) {
      console.error('Error fetching project views/spaces:', err);
    }
  };

  const fetchNotes = async () => {
    try {
      setLoadingNotes(true);
      const fetchedNotes = await getNotes(workspaceId, 'model', modelId);
      setNotes(fetchedNotes);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleEdit = (fieldName: string, currentValue: any) => {
    setEditingField(fieldName);
    setEditValue(currentValue || '');
  };

  const handleSave = async (fieldName: string) => {
    try {
      await modelService.update(workspaceId, modelId, {
        [fieldName]: editValue
      });
      setModel({ ...model!, [fieldName]: editValue });
      setEditingField(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update model');
      console.error('Error updating model:', err);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Basic Information handlers
  const handleEditBasicInfo = () => {
    if (!model) return;

    setBasicInfoData({
      name: model.name || '',
      bed: model.bed?.toString() || '',
      bath: model.bath?.toString() || '',
      area: model.area?.toString() || '',
      outdoorType: model.outdoorType || '',
      outdoorArea: model.outdoorArea?.toString() || '',
      ceiling: model.ceiling?.toString() || '',
      type: model.type || '',
      modelCollection: model.modelCollection || '',
      den: model.den?.toString() || '',
      media: model.media?.toString() || '',
      flex: model.flex?.toString() || ''
    });
    setEditingBasicInfo(true);
  };

  const handleCancelBasicInfo = () => {
    setEditingBasicInfo(false);
  };

  const handleBasicInfoChange = (field: string, value: string) => {
    setBasicInfoData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBasicInfo = async () => {
    if (!model) return;

    try {
      setSavingBasicInfo(true);

      const updateData: any = {
        name: basicInfoData.name.trim(),
        type: basicInfoData.type.trim() || undefined,
        modelCollection: basicInfoData.modelCollection.trim() || undefined,
        outdoorType: basicInfoData.outdoorType.trim() || undefined
      };

      // Convert numeric fields
      if (basicInfoData.bed) updateData.bed = parseInt(basicInfoData.bed);
      if (basicInfoData.bath) updateData.bath = parseInt(basicInfoData.bath);
      if (basicInfoData.area) updateData.area = parseFloat(basicInfoData.area);
      if (basicInfoData.outdoorArea) updateData.outdoorArea = parseFloat(basicInfoData.outdoorArea);
      if (basicInfoData.ceiling) updateData.ceiling = parseFloat(basicInfoData.ceiling);
      if (basicInfoData.den) updateData.den = parseInt(basicInfoData.den);
      if (basicInfoData.media) updateData.media = parseInt(basicInfoData.media);
      if (basicInfoData.flex) updateData.flex = parseInt(basicInfoData.flex);

      const response = await modelService.update(workspaceId, modelId, updateData);

      // Update local state
      setModel(response.model);
      setEditingBasicInfo(false);
    } catch (err: any) {
      console.error('Error updating basic info:', err);
      toast.error(err.response?.data?.message || 'Failed to update basic information');
    } finally {
      setSavingBasicInfo(false);
    }
  };

  // Views handlers
  const handleEditViews = () => {
    if (!model) return;
    setViewsData(model.views || []);
    setEditingViews(true);
  };

  const handleCancelViews = () => {
    setEditingViews(false);
  };

  const handleViewsChange = (views: string[]) => {
    setViewsData(views);
  };

  const handleAddViewsFromModal = async (newViews: string[], customViews: string[] = []) => {
    // Add selected views to viewsData
    setViewsData(prev => [...prev, ...newViews]);

    // If there are custom views, add them to the project as well
    if (customViews.length > 0 && model?.projectId) {
      try {
        const newProjectViews = [...projectViews, ...customViews.filter(v => !projectViews.includes(v))];
        await api.put(`/workspaces/${workspaceId}/projects/${model.projectId}`, {
          views: newProjectViews
        });
        setProjectViews(newProjectViews);
      } catch (err: any) {
        console.error('Error adding views to project:', err);
        toast.error('Failed to add custom views to project');
      }
    }
  };

  const handleSaveViews = async () => {
    if (!model) return;

    try {
      setSavingViews(true);
      const response = await modelService.update(workspaceId, modelId, {
        views: viewsData
      });

      setModel(response.model);
      setEditingViews(false);
    } catch (err: any) {
      console.error('Error updating views:', err);
      toast.error(err.response?.data?.message || 'Failed to update views');
    } finally {
      setSavingViews(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!model) return;
    setSpacesData(model.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (spaces: ModelSpace[]) => {
    setSpacesData(spaces);
  };

  const handleAddSpacesFromModal = async (newSpaces: string[], customSpaces: string[] = []) => {
    // Add selected spaces to spacesData (convert strings to ModelSpace objects)
    const newSpaceObjects: ModelSpace[] = newSpaces.map(name => ({ name, size: undefined }));
    setSpacesData(prev => [...prev, ...newSpaceObjects]);

    // If there are custom spaces, add them to the project as well
    if (customSpaces.length > 0 && model?.projectId) {
      try {
        const newProjectSpaces = [...projectSpaces, ...customSpaces.filter(s => !projectSpaces.includes(s))];
        await api.put(`/workspaces/${workspaceId}/projects/${model.projectId}`, {
          spaces: newProjectSpaces
        });
        setProjectSpaces(newProjectSpaces);
      } catch (err: any) {
        console.error('Error adding spaces to project:', err);
        toast.error('Failed to add custom spaces to project');
      }
    }
  };

  const handleAddSpacesWithSizes = async (newSpaces: Array<{ name: string; size: string }>, customSpaces: string[] = []) => {
    // Add selected spaces with sizes to spacesData (convert size strings to numbers)
    const spacesWithNumberSizes: ModelSpace[] = newSpaces.map(s => ({
      name: s.name,
      size: s.size ? parseFloat(s.size) : undefined
    }));
    setSpacesData(prev => [...prev, ...spacesWithNumberSizes]);

    // If there are custom spaces, add them to the project as well
    if (customSpaces.length > 0 && model?.projectId) {
      try {
        const newProjectSpaces = [...projectSpaces, ...customSpaces.filter(s => !projectSpaces.includes(s))];
        await api.put(`/workspaces/${workspaceId}/projects/${model.projectId}`, {
          spaces: newProjectSpaces
        });
        setProjectSpaces(newProjectSpaces);
      } catch (err: any) {
        console.error('Error adding spaces to project:', err);
        toast.error('Failed to add custom spaces to project');
      }
    }
  };

  const handleSaveSpaces = async () => {
    if (!model) return;

    try {
      setSavingSpaces(true);
      const response = await modelService.update(workspaceId, modelId, {
        spaces: spacesData
      });

      setModel(response.model);
      setEditingSpaces(false);
    } catch (err: any) {
      console.error('Error updating spaces:', err);
      toast.error(err.response?.data?.message || 'Failed to update spaces');
    } finally {
      setSavingSpaces(false);
    }
  };

  // Notes handlers
  const handleAddNote = () => {
    setNoteContent('');
    setShowNoteDialog(true);
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim()) {
      toast.warning('Please enter note content');
      return;
    }

    try {
      setSavingNote(true);
      const newNote = await createNote(workspaceId, 'model', modelId, {
        content: noteContent
      });
      setNotes(prev => [newNote, ...prev]);
      setShowNoteDialog(false);
      setNoteContent('');
    } catch (err: any) {
      console.error('Error saving note:', err);
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote(workspaceId, noteId);
      setNotes(prev => prev.filter(n => n._id !== noteId));
      setDeleteNoteId(null);
    } catch (err: any) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
    }
  };

  // Video handlers
  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    try {
      const newVideo = await videoService.createVideo(workspaceId, 'model', modelId, {
        title: video.title,
        url: video.url,
        type: video.type,
        thumbnail: video.thumbnail
      });

      setVideos(prev => [...prev, newVideo]);
    } catch (error: any) {
      console.error('Error adding video:', error);
      throw new Error(error.response?.data?.message || 'Failed to add video');
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await videoService.deleteVideo(workspaceId, videoId);
      setVideos(prev => prev.filter(v => v._id !== videoId));
    } catch (error: any) {
      console.error('Error deleting video:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete video');
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    try {
      await videoService.updateVideo(workspaceId, videoId, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (error: any) {
      console.error('Error updating video:', error);
      throw new Error(error.response?.data?.message || 'Failed to update video');
    }
  };

  // Delete model handler
  const handleDeleteModel = async () => {
    try {
      setDeleting(true);
      await modelService.delete(workspaceId, modelId);
      toast.success('Model deleted successfully');
      if (onSuccess) {
        onSuccess(); // Trigger parent refresh
      }
      onClose(); // Close slider
    } catch (err: any) {
      console.error('Error deleting model:', err);
      toast.error(err.response?.data?.message || 'Failed to delete model');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Custom Attributes handlers
  const handleEditCustomAttributes = () => {
    if (!model) return;
    setCustomAttributesData(model.customAttributes || []);
    setEditingCustomAttributes(true);
  };

  const handleCancelCustomAttributes = () => {
    setEditingCustomAttributes(false);
  };

  const handleAddCustomAttribute = () => {
    setCustomAttributesData(prev => [...prev, { key: '', value: '' }]);
  };

  const handleCustomAttributeChange = (index: number, field: 'key' | 'value', value: string) => {
    setCustomAttributesData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDeleteCustomAttribute = (index: number) => {
    setCustomAttributesData(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveCustomAttributes = async () => {
    if (!model) return;

    // Filter out empty attributes
    const validAttributes = customAttributesData.filter(attr => attr.key.trim() !== '');

    try {
      setSavingCustomAttributes(true);
      const response = await modelService.update(workspaceId, modelId, {
        customAttributes: validAttributes
      });

      setModel(response.model);
      setEditingCustomAttributes(false);
    } catch (err: any) {
      console.error('Error updating custom attributes:', err);
      toast.error(err.response?.data?.message || 'Failed to update custom attributes');
    } finally {
      setSavingCustomAttributes(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    // If more than 5 days, show full date and time
    if (diffInDays > 5) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // If today
    if (diffInDays === 0) {
      if (diffInHours === 0) {
        if (diffInMinutes === 0) {
          return 'Just now';
        }
        return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
      }
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    // If within 5 days
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };


  if (loading) {
    return (
      <div className="model-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="model-details-slider">
        <div className="model-details-error">
          <p>{error || 'Model not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const renderEditableField = (label: string, fieldName: string, value: any, type: string = 'text') => {
    const isEditing = editingField === fieldName;

    return (
      <div className="model-detail-item">
        <div className="model-detail-label">{label}</div>
        <div className="model-detail-value">
          {isEditing ? (
            <div className="model-detail-edit">
              <input
                type={type}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="form-input"
                autoFocus
              />
              <div className="model-detail-edit-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleCancel}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => handleSave(fieldName)}>
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="model-detail-display" onClick={() => handleEdit(fieldName, value)}>
              <span>{value || '-'}</span>
              <span className="model-detail-edit-icon" style={{ display: 'flex', alignItems: 'center', color: '#48182F' }}>
                <Pencil size={16} />
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="model-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>Model Collection: {model.modelCollection || 'N/A'}</h2>
            <p>Model Name: {model.name}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="model-details-content">
        {/* Left Column */}
        <div className="model-details-left">
          {/* Basic Information Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Basic Information</h3>
              {!editingBasicInfo && (
                <button className="model-details-tile-edit" onClick={handleEditBasicInfo} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingBasicInfo ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-grid">
                    <div className="model-details-field">
                      <label>Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={basicInfoData.name}
                        onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Collection</label>
                      <input
                        type="text"
                        className="form-input"
                        value={basicInfoData.modelCollection}
                        onChange={(e) => handleBasicInfoChange('modelCollection', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Type</label>
                      <input
                        type="text"
                        className="form-input"
                        value={basicInfoData.type}
                        onChange={(e) => handleBasicInfoChange('type', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Bedrooms</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.bed}
                        onChange={(e) => handleBasicInfoChange('bed', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Bathrooms</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.bath}
                        onChange={(e) => handleBasicInfoChange('bath', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Den</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.den}
                        onChange={(e) => handleBasicInfoChange('den', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Media</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.media}
                        onChange={(e) => handleBasicInfoChange('media', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Flex</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.flex}
                        onChange={(e) => handleBasicInfoChange('flex', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Area (Sq.Ft)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.area}
                        onChange={(e) => handleBasicInfoChange('area', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Outdoor Type</label>
                      <input
                        type="text"
                        className="form-input"
                        value={basicInfoData.outdoorType}
                        onChange={(e) => handleBasicInfoChange('outdoorType', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Outdoor Area (Sq.Ft)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.outdoorArea}
                        onChange={(e) => handleBasicInfoChange('outdoorArea', e.target.value)}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Ceiling (Ft.)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoData.ceiling}
                        onChange={(e) => handleBasicInfoChange('ceiling', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelBasicInfo}
                    disabled={savingBasicInfo}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveBasicInfo}
                    disabled={savingBasicInfo}
                  >
                    {savingBasicInfo ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Name</div>
                  <div className="model-detail-value">{model.name || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Collection</div>
                  <div className="model-detail-value">{model.modelCollection || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Type</div>
                  <div className="model-detail-value">{model.type || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Bedrooms</div>
                  <div className="model-detail-value">{model.bed || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Bathrooms</div>
                  <div className="model-detail-value">{model.bath || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Den</div>
                  <div className="model-detail-value">{model.den || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Media</div>
                  <div className="model-detail-value">{model.media || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Flex</div>
                  <div className="model-detail-value">{model.flex || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Area (Sq.Ft)</div>
                  <div className="model-detail-value">{model.area || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Outdoor Type</div>
                  <div className="model-detail-value">{model.outdoorType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Outdoor Area (Sq.Ft)</div>
                  <div className="model-detail-value">{model.outdoorArea || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Ceiling (Ft.)</div>
                  <div className="model-detail-value">{model.ceiling || '-'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Floor Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="model"
                entityId={modelId}
                category="floor_layout"
              />
            </div>
          </div>

          {/* Suite Customization Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Suite Customization Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="model"
                entityId={modelId}
                category="suite_customization"
              />
            </div>
          </div>

          {/* Video Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Video Gallery</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => videoGalleryRef.current?.openAddDialog()}
              >
                <LinkIcon size={16} style={{ marginRight: '6px' }} />
                Add Video
              </button>
            </div>
            <div className="model-details-tile-content">
              <VideoGallery
                ref={videoGalleryRef}
                workspaceId={workspaceId}
                entityType="model"
                entityId={modelId}
                videos={videos}
                onAddVideo={handleAddVideo}
                onDeleteVideo={handleDeleteVideo}
                onUpdateVideo={handleUpdateVideo}
                hideHeader={true}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="model-details-right">
          {/* Views and Spaces Row */}
          <div className="model-details-row">
            {/* Views Tile */}
            <div className="model-details-tile model-details-tile-25">
            <div className="model-details-tile-header">
              <h3>Views</h3>
              {!editingViews && (
                <button className="model-details-tile-edit" onClick={handleEditViews} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingViews ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-sortable-container">
                    <SortableList
                      items={viewsData}
                      onChange={handleViewsChange}
                      placeholder="Add custom view name"
                      addButtonText="+"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddViewsModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      + Add from List
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelViews}
                    disabled={savingViews}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveViews}
                    disabled={savingViews}
                  >
                    {savingViews ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {model.views && model.views.length > 0 ? (
                  <div className="model-details-list">
                    {model.views.map((view, index) => (
                      <div key={index} className="model-details-list-item">
                        {view}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No views found</p>
                  </div>
                )}
              </div>
            )}
            </div>

            {/* Spaces Tile */}
            <div className="model-details-tile model-details-tile-25">
            <div className="model-details-tile-header">
              <h3>Spaces</h3>
              {!editingSpaces && (
                <button className="model-details-tile-edit" onClick={handleEditSpaces} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingSpaces ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-sortable-container">
                    {spacesData.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {spacesData.map((space, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ flex: 1 }}>{space.name}</span>
                            <input
                              type="number"
                              className="form-input"
                              placeholder="Size"
                              value={space.size ?? ''}
                              onChange={(e) => {
                                const newSpaces = [...spacesData];
                                const newSize = e.target.value ? parseFloat(e.target.value) : undefined;
                                newSpaces[index] = { ...space, size: newSize };
                                setSpacesData(newSpaces);
                              }}
                              style={{ width: '100px' }}
                            />
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setSpacesData(spacesData.filter((_, i) => i !== index));
                              }}
                              style={{ padding: '4px 8px' }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No spaces added yet.</p>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddSpacesModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      + Add from List
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelSpaces}
                    disabled={savingSpaces}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveSpaces}
                    disabled={savingSpaces}
                  >
                    {savingSpaces ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {model.spaces && model.spaces.length > 0 ? (
                  <div className="model-details-list">
                    {model.spaces.map((space, index) => (
                      <div key={index} className="model-details-list-item">
                        {space.name}{space.size ? ` (${space.size})` : ''}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No spaces found</p>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          {/* Notes Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Notes</h3>
              <button className="btn btn-primary btn-sm" onClick={handleAddNote}>Add</button>
            </div>
            <div className="model-details-tile-content">
              {loadingNotes ? (
                <div className="model-details-loading">Loading notes...</div>
              ) : notes.length > 0 ? (
                <div className="model-details-notes-list">
                  {notes.map(note => (
                    <div key={note._id} className="model-details-note-item">
                      <div className="model-details-note-header">
                        <div className="model-details-note-meta">
                          <span className="model-details-note-author">
                            By {note.createdByName || note.createdBy}
                          </span>
                          <span className="model-details-note-separator">•</span>
                          <span className="model-details-note-date">
                            {formatDate(note.createdAt)}
                          </span>
                        </div>
                        <button
                          className="model-details-note-delete"
                          onClick={() => setDeleteNoteId(note._id)}
                          title="Delete note"
                        >
                          ✕
                        </button>
                      </div>
                      <div
                        className="model-details-note-content"
                        dangerouslySetInnerHTML={{ __html: note.content }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="model-details-empty">
                  <p>No notes found</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Documents</h3>
            </div>
            <div className="model-details-tile-content">
              <DocumentGallery
                workspaceId={workspaceId}
                entityType="model"
                entityId={modelId}
                fileType="document"
                acceptTypes=".pdf,.doc,.docx"
                helpText="All model related documents can be uploaded here. PDF and Word files are accepted."
              />
            </div>
          </div>

          {/* Custom Attributes Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Custom Attributes</h3>
              {!editingCustomAttributes && (
                <button
                  className="model-details-tile-edit"
                  onClick={handleEditCustomAttributes}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingCustomAttributes ? (
              <>
                <div className="model-details-tile-content">
                  <div className="custom-attributes-editor">
                    {customAttributesData.length > 0 ? (
                      <div className="custom-attributes-list">
                        {customAttributesData.map((attr, index) => (
                          <div key={index} className="custom-attribute-row">
                            <div className="custom-attribute-inputs">
                              <input
                                type="text"
                                className="form-input"
                                placeholder="Attribute name"
                                value={attr.key}
                                onChange={(e) => handleCustomAttributeChange(index, 'key', e.target.value)}
                              />
                              <input
                                type="text"
                                className="form-input"
                                placeholder="Value"
                                value={attr.value || ''}
                                onChange={(e) => handleCustomAttributeChange(index, 'value', e.target.value)}
                              />
                            </div>
                            <button
                              type="button"
                              className="custom-attribute-delete"
                              onClick={() => handleDeleteCustomAttribute(index)}
                              title="Delete attribute"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="model-details-empty">
                        <p>No custom attributes yet</p>
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleAddCustomAttribute}
                      style={{ marginTop: '12px' }}
                    >
                      + Add Attribute
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelCustomAttributes}
                    disabled={savingCustomAttributes}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveCustomAttributes}
                    disabled={savingCustomAttributes}
                  >
                    {savingCustomAttributes ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {model.customAttributes && model.customAttributes.length > 0 ? (
                  <div className="model-details-attributes">
                    {model.customAttributes.map((attr, index) => (
                      <div key={index} className="model-detail-item">
                        <div className="model-detail-label">{attr.key}</div>
                        <div className="model-detail-value">{attr.value || '-'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No custom attributes</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Electrical Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Electrical Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="model"
                entityId={modelId}
                category="electrical_layout"
              />
            </div>
          </div>

          {/* Photo Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Photo Gallery</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="model"
                entityId={modelId}
                category="photo_gallery"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="model-details-footer">
        <button
          className="btn btn-danger btn-sm"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Model
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {/* Add Views Modal */}
      <AddItemsModal
        isOpen={showAddViewsModal}
        onClose={() => setShowAddViewsModal(false)}
        onAdd={handleAddViewsFromModal}
        availableOptions={projectViews.filter(option => !viewsData.includes(option))}
        title="Add Views"
        customInputPlaceholder="Enter custom view name..."
        emptyMessage="No views configured for this project. Add a custom view below."
      />

      {/* Add Spaces Modal */}
      <AddItemsModal
        isOpen={showAddSpacesModal}
        onClose={() => setShowAddSpacesModal(false)}
        onAdd={handleAddSpacesFromModal}
        onAddWithSizes={handleAddSpacesWithSizes}
        availableOptions={projectSpaces.filter(option => !spacesData.some(s => s.name === option))}
        title="Add Spaces"
        customInputPlaceholder="Enter custom space name..."
        emptyMessage="No spaces configured for this project. Add a custom space below."
        showSizeInput={true}
        sizePlaceholder="Size"
      />

      {/* Add Note Dialog */}
      {showNoteDialog && (
        <div className="model-details-modal-overlay" onClick={() => setShowNoteDialog(false)}>
          <div className="model-details-modal model-details-note-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Add Note</h3>
            <RichTextEditor
              content={noteContent}
              onChange={setNoteContent}
              placeholder="Enter note content..."
            />
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowNoteDialog(false)}
                disabled={savingNote}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveNote}
                disabled={savingNote}
              >
                {savingNote ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Note Confirmation */}
      <ConfirmDialog
        isOpen={deleteNoteId !== null}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={() => deleteNoteId && handleDeleteNote(deleteNoteId)}
        onClose={() => setDeleteNoteId(null)}
      />

      {/* Delete Model Confirmation */}
      {showDeleteModal && (
        <div className="model-details-modal-overlay">
          <div className="model-details-modal">
            <h3>Delete Model</h3>
            <p>Are you sure you want to delete this model? This action cannot be undone.</p>
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-md"
                onClick={handleDeleteModel}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change History FAB Button */}
      <ChangeHistoryButton
        onClick={() => setHistoryDrawerOpen(true)}
        isOpen={historyDrawerOpen}
      />

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        workspaceId={workspaceId}
        entityType="model"
        entityId={modelId}
      />
    </div>
  );
};

export default ModelDetailsSlider;
